import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DataSetsRecordButtons from 'components/DataSetsRecordButtons/DataSetsRecordButtons';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import ProfitCenterLinksCustomers from 'components/ProfitCenterLinksCustomers/ProfitCenterLinksCustomers';
import Separator from 'components/Separator/Separator';

import GeneralCustomerInfo from './components/GeneralCustomerInfo';

import type { ICreateCustomerView } from './interfaces/ICreateCustomerView';

import styles from './sass/CreateCustomerView.module.scss';

function CreateCustomerView({
  control, onCreateCustomerSubmit, isConfirmationModalOpen, onHandleCloseConfirmation,
  onDiscardChangesConfirm, enumData, naceCodesData, naceDivisions, onNaceSelectionChange, isPageLoading,
  onFindCountries, onFindStates, onFindCities, isStatesDisabled, isCitiesDisabled, onChangeCountry, onChangeState,
  dateOpened, onChangeDate, profitCenterIds, watch, setValue, defaultCountries,
  handleGetVcOptions, vcOptionsList, listPosition,
}: ICreateCustomerView) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const placeholder = recordId ? t('common.notProvided') : '';

  return (
    <div className={styles.root}>
      <Panel className={styles.panel}>
        <div className={styles.wrapper}>
          <Loader isPageLoading={isPageLoading} isFullLoader={false} />
          <GeneralCustomerInfo
            control={control}
            enumData={enumData}
            placeholder={placeholder}
            recordId={recordId}
            dateOpened={dateOpened}
            onChangeDate={onChangeDate}
            isPageLoading={isPageLoading}
            handleGetVcOptions={handleGetVcOptions}
            vcOptionsList={vcOptionsList}
            listPosition={listPosition}
            setValue={setValue}
          />
          <DetailSection
            control={control}
            naceCodesData={naceCodesData}
            naceDivisions={naceDivisions}
            onNaceSelectionChange={onNaceSelectionChange}
            placeholder={placeholder}
            recordId={recordId}
          />
          <DataSetsPanel title={t('customers.selections.financials')} className={styles.fullWidth}>
            <ProfitCenterLinksCustomers
              control={control}
              profitCenterIds={profitCenterIds}
              placeholder={placeholder}
              watch={watch}
              setValue={setValue}
            />
          </DataSetsPanel>
          <AddressPanel
            control={control}
            placeholder={placeholder}
            onFindCountries={onFindCountries}
            onFindStates={onFindStates}
            onFindCities={onFindCities}
            isStatesDisabled={isStatesDisabled}
            isCitiesDisabled={isCitiesDisabled}
            onChangeCountry={onChangeCountry}
            onChangeState={onChangeState}
            defaultCountries={defaultCountries}
          />
        </div>
        <Separator />
        <Modal
          open={isConfirmationModalOpen}
          title={t('createNewUser.cancelModalTitle')}
          description={t('clients.cancelModalText')}
          confirmTitle={t('common.discardChanges')}
          closeTitle={t('common.keepEditing')}
          confirmVariant="error"
          onClose={() => onHandleCloseConfirmation(false)}
          onReject={() => onHandleCloseConfirmation(false)}
          onConfirm={onDiscardChangesConfirm}
          isLoading={isPageLoading}
        />
        <DataSetsRecordButtons
          onClose={onHandleCloseConfirmation}
          onSubmit={onCreateCustomerSubmit}
          isLoading={isPageLoading}
        />
      </Panel>
    </div>
  );
}

export default CreateCustomerView;
